import { useState } from 'react'
import { useSortable } from '@lunary/ui-web/use-sortable'
import type { AdminReadingTopicDto } from '@lunary/api-client'
import { useAdminAuth } from '../../shared/auth/admin-auth-context'
import { ConfirmDialog } from '../../shared/ui/confirm-dialog'
import { TopicDialog } from './topic-dialog'
import { topicErrorText, useCreateTopic, useDeleteTopic, useReadingTopics, useReorderTopics, useUpdateTopic } from './queries'
import expert from '../../shared/ui/expert.module.css'
import styles from './reading-topics.module.css'

type Editing = { kind: 'new' } | { kind: 'edit'; topic: AdminReadingTopicDto } | null

export function ReadingTopicsPage() {
  const { user, status } = useAdminAuth()
  const staff = status === 'authenticated' && (user?.role === 'admin' || user?.role === 'expert')
  const topics = useReadingTopics()
  const create = useCreateTopic()
  const update = useUpdateTopic()
  const reorder = useReorderTopics()
  const remove = useDeleteTopic()
  const [editing, setEditing] = useState<Editing>(null)
  const [deleting, setDeleting] = useState<AdminReadingTopicDto | null>(null)
  const items = topics.data?.items ?? []
  const enabledCount = items.filter((t) => t.enabled).length

  const sortable = useSortable({
    keys: items.map((t) => t.slug),
    onReorder: (slugs) => reorder.mutate(slugs),
  })

  if (!staff) return <p className="error-box">Раздел доступен только сотрудникам.</p>

  const error = [topics.error, update.error, reorder.error].find((e) => e) ?? null

  return (
    <div>
      <header className={expert.head}>
        <div>
          <h1>Темы разборов</h1>
          <p className="muted">
            Темы, из которых человек выбирает при заказе разбора. Выключенная или удалённая тема пропадает из выбора, а старые разборы с ней остаются как есть.
          </p>
        </div>
        <button type="button" onClick={() => setEditing({ kind: 'new' })}>
          Добавить тему
        </button>
      </header>
      <section className={expert.panel} aria-labelledby="topics-list">
        <div className={expert.panelHead}>
          <h2 id="topics-list">Темы</h2>
        </div>
        {error && (
          <p className="error-box" role="alert">
            {topicErrorText(error)}
          </p>
        )}
        <div className={styles.tableWrap}>
          <div className={styles.table} role="table" aria-label="Темы разборов">
            <div className={`${styles.row} ${styles.head}`} role="row">
              <span role="columnheader">Порядок</span>
              <span role="columnheader">Название</span>
              <span role="columnheader" className={styles.hintCol}>
                Подсказка
              </span>
              <span role="columnheader">Разборов</span>
              <span role="columnheader">Включена</span>
              <span />
            </div>
            <div {...sortable.containerProps} role="rowgroup">
              {items.map((t) => {
                const lastEnabled = t.enabled && enabledCount === 1
                return (
                  <div key={t.slug} {...sortable.itemProps(t.slug)} role="row" className={`${styles.row} ${t.enabled ? '' : styles.off}`}>
                    <span role="cell">
                      <button
                        className={styles.grip}
                        {...sortable.handleProps(t.slug, `Переместить тему «${t.title}»: пробел, затем стрелки вверх и вниз`)}
                        title="Потяните, чтобы изменить порядок"
                      >
                        ⋮⋮
                      </button>
                    </span>
                    <span role="cell">
                      <b>{t.title}</b>
                      <div className={styles.slug}>{t.slug}</div>
                    </span>
                    <span role="cell" className={`${styles.hintCol} ${styles.muted}`}>
                      {t.hint || '—'}
                    </span>
                    <span role="cell">{t.orders_count}</span>
                    <span role="cell">
                      <button
                        type="button"
                        role="switch"
                        className={styles.switch}
                        aria-checked={t.enabled}
                        aria-label={`Тема «${t.title}» включена`}
                        title={lastEnabled ? 'Нужна хотя бы одна включённая тема' : undefined}
                        disabled={lastEnabled || update.isPending}
                        onClick={() => update.mutate({ slug: t.slug, patch: { enabled: !t.enabled } })}
                      />
                    </span>
                    <span role="cell" className={styles.actions}>
                      <button type="button" className={`secondary ${styles.small}`} onClick={() => setEditing({ kind: 'edit', topic: t })}>
                        Изменить
                      </button>
                      <button
                        type="button"
                        className={`secondary ${styles.small}`}
                        disabled={lastEnabled}
                        title={lastEnabled ? 'Нужна хотя бы одна включённая тема' : undefined}
                        onClick={() => setDeleting(t)}
                      >
                        Удалить
                      </button>
                    </span>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
        {topics.isSuccess && items.length === 0 && <p className="muted">Тем нет. Добавьте первую и включите её.</p>}
      </section>
      <TopicDialog
        open={editing !== null}
        topic={editing?.kind === 'edit' ? editing.topic : null}
        pending={create.isPending || update.isPending}
        error={editing?.kind === 'new' && create.isError ? topicErrorText(create.error) : editing?.kind === 'edit' && update.isError ? topicErrorText(update.error) : null}
        onClose={() => {
          setEditing(null)
          create.reset()
          update.reset()
        }}
        onSubmit={(value) => {
          if (editing?.kind === 'edit') {
            update.mutate({ slug: value.slug, patch: { title: value.title, hint: value.hint } }, { onSuccess: () => setEditing(null) })
          } else {
            create.mutate(value, { onSuccess: () => setEditing(null) })
          }
        }}
      />
      <ConfirmDialog
        open={deleting !== null}
        title={`Удалить тему «${deleting?.title ?? ''}»?`}
        text={
          deleting && deleting.orders_count > 0
            ? `Тема пропадёт из выбора и из этого списка. По ней уже ${deleting.orders_count} разб., они сохранятся и откроются как раньше, с этим названием.`
            : 'Тема пропадёт из выбора и из этого списка. Разборов по ней нет. Код останется занятым.'
        }
        confirmLabel="Удалить"
        danger
        pending={remove.isPending}
        error={remove.isError ? topicErrorText(remove.error) : null}
        onClose={() => {
          setDeleting(null)
          remove.reset()
        }}
        onConfirm={() => {
          if (deleting) remove.mutate(deleting.slug, { onSuccess: () => setDeleting(null) })
        }}
      />
    </div>
  )
}
